import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { P2Alert, P2Button, P2Checkbox, P2TextInput, P2TextLabel } from 'react-bootstrap-common-script'
import { LegalRepresentation, PrivateSolicitor } from '../../../models/classes/ApplicationStep3.class'
import { LoanTypeEnum } from '../../../models/enum/loan-type.enum';
import { namePattern } from '../../../utils/shared-pattern';
import { HDBLinks } from '../../../utils/shared/links';


interface PrivateSolicitorFormProps {
  legal_representation?: LegalRepresentation;
  ConveyancingSetFormValue?: Function;
  user_type: "current" | "proposed" | "outgoing";
  errors?: any;
  variant?: "default" | "conveyancing" | "review" | "mortgage";
  loan_type?: LoanTypeEnum;
  display_form_only?: boolean,
  handleToggle?: () => void;
  forceError?: boolean
}

function PrivateSolicitorForm({variant="default", display_form_only=false, ...props }: PrivateSolicitorFormProps) {
  const [saveAlertShow, setSaveAlertShow] = useState<boolean>(false);
  const accordionEventRef = useRef<any>();

  const handleClickOutside = useCallback((event:any) => {
    if (accordionEventRef.current && !accordionEventRef.current.contains(event.target)) {
      setSaveAlertShow(true);
    }
    else{
      setSaveAlertShow(false);
    }
  },[]);

  useEffect(() => {
    // Add click event listener to the document
    document.addEventListener('click', handleClickOutside);

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]); 

  const solicitor = variant === "conveyancing" ? props.legal_representation?.conveyancing_private_solicitor : props.legal_representation?.private_solicitor;
  const isBtnEnable = useMemo(() => {
    return props.errors?.uen.isValid && props.errors?.name_of_firm.isValid && props.errors?.email_of_solicitor.isValid;
  }, [props.errors]);

  const privateSolicitorFormHandler = useCallback((value:string | boolean,field_name:string) =>{
    if (props.ConveyancingSetFormValue && (variant === "default" || variant === "review" || variant === "mortgage")) {
      //review mean this will be used under STAGE B
      let represented_same_solicitor = props.loan_type === LoanTypeEnum.no_loan ? true : solicitor?.represented_same_solicitor;
      let legalRep = new LegalRepresentation({ ...props.legal_representation, private_solicitor: new PrivateSolicitor({ ...props.legal_representation?.private_solicitor,represented_same_solicitor, [field_name.toString()]: value }) })
      props.ConveyancingSetFormValue({ [props.user_type.toString()]: legalRep })
    }
    if (props.ConveyancingSetFormValue && variant === "conveyancing") {
      let legalRep = new LegalRepresentation({ ...props.legal_representation, conveyancing_private_solicitor: new PrivateSolicitor({ ...props.legal_representation?.conveyancing_private_solicitor, [field_name.toString()]: value }) })
      props.ConveyancingSetFormValue({ [props.user_type.toString()]: legalRep })
    }
  },[props]);

  const handleValueChange = (key: string) => (value: string) => {
    privateSolicitorFormHandler(value,key);
  }

  const checkConveyancingSameSolicitor = useCallback(() => {
    if (props.ConveyancingSetFormValue) {
      let legalRep = new LegalRepresentation({ ...props.legal_representation, private_solicitor: new PrivateSolicitor({ ...solicitor, represented_same_solicitor: !solicitor?.represented_same_solicitor }) })
      props.ConveyancingSetFormValue({ [props.user_type.toString()]: legalRep })
    }
  },[solicitor]);

  const confirmOrEditSolicitor = useCallback(() => {
    if (props.ConveyancingSetFormValue) {
      let legalRep = new LegalRepresentation({ ...props.legal_representation, private_solicitor: new PrivateSolicitor({ ...solicitor, is_confirm: !solicitor?.is_confirm }) })
      props.ConveyancingSetFormValue({ [props.user_type.toString()]: legalRep })
    }

    if(solicitor?.is_confirm !== true){
       props.handleToggle?.();
    }
    privateSolicitorFormHandler(!solicitor?.is_confirm,'is_confirm');
  },[solicitor]);



  const saveAlertVisible = useMemo(()=>{
    if(!display_form_only && saveAlertShow && isBtnEnable && !solicitor?.is_confirm){
      return true;
    }
    else{
      return false;
    }
  },[isBtnEnable, saveAlertShow, solicitor?.is_confirm])

  return (
      <div ref={accordionEventRef}>
        <div className='mt-3'>
          <P2TextInput pattern={namePattern} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.name_of_firm?.isValid} errorMsg={props.errors?.name_of_firm?.message} required value={solicitor?.name_of_firm} name='name_of_firm' maxLength={100} label={`Name of the law firm`} placeholder={"Enter value"} type='text' onChange={handleValueChange("name_of_firm")} />
        </div>
        <div className='mt-3'>
          <P2TextInput forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.email_of_solicitor?.isValid} errorMsg={props.errors?.email_of_solicitor?.message} required value={solicitor?.email_of_solicitor} name='email_of_solicitor' maxLength={100} label={`Email address of the lawyer`} placeholder={"Enter value"} type='text' onChange={handleValueChange("email_of_solicitor")} />
        </div>
        <div className='mt-3'>
          <P2TextInput forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.uen.isValid} errorMsg={props.errors?.uen.message} required value={solicitor?.uen} name='uen' maxLength={50} label={`UEN of the law firm`} placeholder={"Enter value"} type='text' onChange={handleValueChange("uen")} />
        </div>
        <div className='mt-2'>
          <P2TextLabel label={<span style={{fontWeight: "500", fontSize: 16, color: "#575757"}} >Check for the <a href={HDBLinks.uen} target="_blank">law firm's UEN</a>.</span>}/>
        </div>
        {/* Block/ House Number */}
        <div className='mt-3'>
          <P2TextInput label={'Block/ House Number'}  forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.block_house_number.isValid} errorMsg={props.errors?.block_house_number.message} required value={solicitor?.block_house_number} name='block_house_number' maxLength={10} placeholder={"Enter value"} type='text' onChange={handleValueChange("block_house_number")} />
        </div>
        {/* Street Name */}
        <div className='mt-3'>
          <P2TextInput label={'Street Name'} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.street_name.isValid} errorMsg={props.errors?.street_name.message} required value={solicitor?.street_name} name='street_name' maxLength={40} placeholder={"Enter value"} type='text' onChange={handleValueChange("street_name")} />
        </div>
        {/* Level and Unit Number */}
        <div className='mt-3 d-flex flex-column flex-md-row gap-3'>
          <div className='flex-grow-1'> 
            <P2TextInput label={'Level'} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.level.isValid} errorMsg={props.errors?.level.message} required value={solicitor?.level} name='level' maxLength={5} placeholder={"Enter value"} type='text' onChange={handleValueChange("level")} />
          </div>
          <div className='flex-grow-1'> 
            <P2TextInput label={'Unit Number'} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.unit_number.isValid} errorMsg={props.errors?.unit_number.message} required value={solicitor?.unit_number} name='unit_number' maxLength={5} placeholder={"Enter value"} type='text' onChange={handleValueChange("unit_number")} />
          </div>
        </div>
        {/* Postal Code */}
        <div className='mt-3'>
          <P2TextInput label={'Postal Code'} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.postal_code.isValid} errorMsg={props.errors?.postal_code.message} required value={solicitor?.postal_code} name='postal_code' maxLength={6} placeholder={"Enter value"} type='text' onChange={handleValueChange("postal_code")} />
        </div>
        {/* Building Name */}
        <div className='mt-3'>
          <P2TextInput label={'Building Name'} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.building_name.isValid} errorMsg={props.errors?.building_name.message} required value={solicitor?.building_name} name='building_name' maxLength={40} placeholder={"Enter value"} type='text' onChange={handleValueChange("building_name")} />
        </div>
        {/* Telephone Number */}
        <div className='mt-3'>
          <P2TextInput label={'Telephone Number'} forceError={props.forceError} disabled={solicitor?.is_confirm} isError={!props.errors?.telephone_number.isValid} errorMsg={props.errors?.telephone_number.message} required value={solicitor?.telephone_number} name='telephone_number' maxLength={8} placeholder={"Enter value"} type='text' onChange={handleValueChange("telephone_number")} />
        </div>
       
        {/* NOTE: This checkbox will only show up when loan type != no loan.
          Therefore, under privateSolicitorFormHandler, represented_same_solicitor will be set to true by default for no_loan.
        */}
        {(variant === "default" && props.loan_type !== LoanTypeEnum.no_loan) && <div className='mt-3'>
          <P2Checkbox variant={solicitor?.is_confirm ? "disabled" : "default"} isActive={solicitor?.represented_same_solicitor} onClick={checkConveyancingSameSolicitor} description={<P2TextLabel tooltipContent={<span style={{color: "#212529", lineHeight: "1.5rem", fontSize: "1rem"}}>You would be required to engage a lawyer for the following,<div>1) Discharge of current mortgage loan</div><div>2) Obtaining fresh mortgage loan</div><div>3) Conveyancing for the ownership change (current owners)</div><div>4) Conveyancing for the ownership change (proposed owners)</div><br /><div>The change in flat ownership is a conveyancing process. As a recommendation, having the same lawyer acting for the process would expedite and reduce the liaising with multiple parties.</div></span>} hasTooltip label={<span className='m-0'>Conveyancing process is represented by the same lawyer as above.</span>} />} />
        </div>}
       

        {saveAlertVisible && <div style={{ marginTop: 24 }}>
          <P2Alert variant={"danger"} text={"Please update details to proceed with the application. "}></P2Alert>
        </div>}

        {!display_form_only && <div style={{ display: "flex", justifyContent: "flex-start", margin: "32px 0 12px" }} className="polymer-button-wrapper">
          <P2Button disabled={!isBtnEnable} onClick={confirmOrEditSolicitor} variant={"outline-primary"}>{solicitor?.is_confirm ? "Update details" : "Confirm lawyer"}</P2Button>
        </div>}
      </div>
  )
}

export default PrivateSolicitorForm
